import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { EnvironmentOutlined, FilterOutlined } from '@ant-design/icons';
import { updateUserSettings } from '../../services/api';
import { captureEvent } from '../../services/analytics';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { showErrorToastFullScreen } from '../../utils/toastUtils';
import { GeoCoverage, MinFitLevel, UserSettings } from '../../types';
import { GEO_OPTIONS, FIT_OPTIONS } from '../../utils/searchPreferences';
import RadioCardGroup from './RadioCardGroup';
import DailyCapField from './DailyCapField';
import css from './SearchPreferencesSection.module.scss';

interface SearchPreferencesSectionProps {
  // Null until the parent's single GET resolves, and null for good if it
  // failed — the controls stay disabled until the real values land, so a
  // click can never save a placeholder default over what the user has.
  settings: UserSettings | null;
  // True only while that GET is in flight — tells the two null states apart.
  loading?: boolean;
  // Lets the parent keep its settings copy fresh (Auto-Apply reads it too).
  onSaved?: (settings: UserSettings) => void;
}

// Option lists live in utils/searchPreferences alongside nextWiderStep, so the
// widen strip on Opportunities computes "one notch wider" from the same
// ordering these radios render.

// Design §13 coaching copy — always visible while a stricter level is chosen.
// Argues from mechanism only: min_fit_level ships with this branch, so any claim
// about what "most successful users" do describes a population of zero.
const STRICT_WARNING =
  'Stricter matching means meaningfully fewer opportunities. Scores are a filter, ' +
  "not a verdict — a 'Potential' match is still worth 2 minutes of your own judgement.";

/**
 * Search Preferences card (design §12 + §13): geo coverage + minimum match
 * level. Each radio change PUTs ONLY that field (partial update), applying
 * the optimistic-update + rollback pattern from the forwardingNotifyMode
 * precedent in Settings.tsx.
 */
const SearchPreferencesSection: React.FC<SearchPreferencesSectionProps> = ({
  settings,
  onSaved,
  loading,
}) => {
  const [geo, setGeo] = useState<GeoCoverage>('nearby');
  const [minFit, setMinFit] = useState<MinFitLevel>('potential_candidate');
  const [geoSaving, setGeoSaving] = useState(false);
  const [fitSaving, setFitSaving] = useState(false);
  const [capSaving, setCapSaving] = useState(false);

  // The radios show placeholder defaults until the GET lands; saving one of
  // those would silently overwrite the user's stored value with a guess.
  const unavailable = settings === null;

  useEffect(() => {
    if (settings) {
      setGeo(settings.geoCoverage);
      setMinFit(settings.minFitLevel);
    }
  }, [settings]);

  const handleGeoChange = async (next: GeoCoverage) => {
    if (unavailable || next === geo || geoSaving) return;
    const previous = geo;
    setGeo(next); // optimistic
    setGeoSaving(true);
    try {
      const updated = await updateUserSettings({ geoCoverage: next });
      setGeo(updated.geoCoverage);
      onSaved?.(updated);
      captureEvent('geo_coverage_changed', { previous, next: updated.geoCoverage });
    } catch (error) {
      setGeo(previous); // rollback
      showErrorToastFullScreen(getApiErrorMessage(error));
    } finally {
      setGeoSaving(false);
    }
  };

  const handleMinFitChange = async (next: MinFitLevel) => {
    if (unavailable || next === minFit || fitSaving) return;
    const previous = minFit;
    setMinFit(next); // optimistic
    setFitSaving(true);
    try {
      const updated = await updateUserSettings({ minFitLevel: next });
      setMinFit(updated.minFitLevel);
      onSaved?.(updated);
      captureEvent('min_fit_level_changed', { previous, next: updated.minFitLevel });
    } catch (error) {
      setMinFit(previous); // rollback
      showErrorToastFullScreen(getApiErrorMessage(error));
    } finally {
      setFitSaving(false);
    }
  };

  // No optimistic write and no local mirror in this component: DailyCapField
  // owns the dragging value, and it only calls back on a real change. Rolling
  // back means letting the field re-seed from the unchanged `settings`.
  const handleCapCommit = async (next: number) => {
    // Narrow on `settings` itself, not the `unavailable` alias — TS can't
    // follow the boolean back to the null check. Deliberately NOT gated on
    // capSaving: the field debounces, so a second commit means the user kept
    // adjusting, and dropping it would strand the slider on a stale value.
    if (!settings) return;
    const previous = settings.dailyOpportunityCap;
    setCapSaving(true);
    try {
      const updated = await updateUserSettings({ dailyOpportunityCap: next });
      onSaved?.(updated);
      captureEvent('daily_opportunity_cap_changed', {
        previous,
        next: updated.dailyOpportunityCap,
      });
    } catch (error) {
      // 422 "Must be between N and M" lands here too. The bounds come from the
      // same payload that renders the slider, so this should be unreachable —
      // surface the server's message rather than inventing one.
      showErrorToastFullScreen(getApiErrorMessage(error));
      // Re-thrown so the field rolls its handle back. The two radio handlers
      // roll back inline because they own their optimistic state; the slider
      // owns its own, so the signal has to travel back to it.
      throw error;
    } finally {
      setCapSaving(false);
    }
  };

  return (
    <section className={css.section}>
      <h2>Search Preferences</h2>
      <p className={css.sectionLead}>
        Tune how wide Job Beast casts the net when finding opportunities for you.
      </p>

      {unavailable && (
        <p className={css.helper} role="status">
          {loading
            ? 'Loading your preferences…'
            : "We couldn't load your preferences. Refresh the page to try again."}
        </p>
      )}

      <DailyCapField
        value={settings?.dailyOpportunityCap ?? null}
        // Bounds ride along with the value on every settings GET, so the
        // control retunes itself if the backend moves them.
        min={settings?.minDailyOpportunityCap ?? 1}
        max={settings?.maxDailyOpportunityCap ?? 1}
        disabled={unavailable}
        saving={capSaving}
        className={css.fieldset}
        onCommit={handleCapCommit}
      />

      <RadioCardGroup
        legend="Search area"
        icon={<EnvironmentOutlined />}
        name="geo-coverage"
        options={GEO_OPTIONS}
        value={geo}
        onChange={handleGeoChange}
        disabled={unavailable}
        saving={geoSaving}
        className={css.fieldset}
      >
        <p className={css.helper}>Applies to new searches and daily refreshes.</p>
      </RadioCardGroup>

      <RadioCardGroup
        legend="Minimum match level"
        icon={<FilterOutlined />}
        name="min-fit-level"
        options={FIT_OPTIONS}
        value={minFit}
        onChange={handleMinFitChange}
        disabled={unavailable}
        saving={fitSaving}
        className={css.fieldset}
        footer={
          minFit !== 'potential_candidate' && (
            <Alert
              type="warning"
              showIcon
              className={css.strictWarning}
              message={STRICT_WARNING}
            />
          )
        }
      >
        <p className={css.helper}>
          The lowest match level worth showing you as an opportunity.
        </p>
      </RadioCardGroup>
    </section>
  );
};

export default SearchPreferencesSection;
